/* eslint-disable @reatom/unit-naming-rule */
import { isAbortError } from '@reatom/framework'

import { Equivalence, Result } from '@blade/std-lib'

import { UnknownError, NotFoundError } from '../../core/Error'

import { reatomFactoryCache } from './reatom-factory-cache'
import { reatomResource } from './reatom-resource'

/**
 * Whether a picture that would not load is worth asking for again.
 *
 * A 404 is an answer, not a failure: most tokens never had a logo mirrored,
 * and the address of one that does is derived rather than stored, so the
 * server has nothing to change its mind with. Retried anyway — exponential
 * backoff caps out and keeps going — every such token asked forever, with a
 * fresh `?retry=` each time so no cache could absorb it. Seen on production
 * 2026-10-02: the same four addresses 404ing every two or three seconds for
 * as long as the page stayed open.
 *
 * Anything else — a timeout, a 5xx, a connection that dropped — is worth
 * another go, and still gets one.
 */
export const worthAskingAgain = (error: unknown): boolean =>
  (error as { _tag?: string } | null)?._tag !== 'NotFoundError'

const reatomImage_ = ({ src }: { src: string }) => {
  const stateAtom = reatomResource<UnknownError | NotFoundError, string>(
    async (ctx, { reason }) => {
      try {
        const resolvedSrc =
          reason === 'retry' ? `${src}?retry=${Date.now()}` : src
        const response = await ctx.schedule(() => fetch(resolvedSrc))
        if (!response.ok) {
          if (response.status === 404) {
            return Result.fail(new NotFoundError())
          }

          return Result.fail(UnknownError.fromUnknown(response.status))
        }
        return Result.succeed(src)
      } catch (error) {
        if (isAbortError(error)) {
          throw error
        }
        return Result.fail(UnknownError.fromUnknown(error))
      }
    },
    {
      eq: Equivalence.String,
      retry: {
        enabled: true,
        strategy: 'exponential',
        retryOn: worthAskingAgain,
      },
    },
  )

  return stateAtom
}

export const reatomImage = reatomFactoryCache({
  factory: reatomImage_,
  keyFn: ({ src }) => src,
  clearOnDisconnect: false,
})
